import { useEffect, useState } from 'react';
import { Button, Chip } from '@nextui-org/react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const NAV = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/users', label: 'Users' },
  { to: '/eventos', label: 'Eventos' },
  { to: '/founders', label: 'Founders' },
  { to: '/popups', label: 'Popups' },
  { to: '/inicio', label: 'Inicio' },
  { to: '/cupones', label: 'Cupones' },
  { to: '/agencias', label: 'Agencias' },
  { to: '/tracking', label: 'Tracking' },
  { to: '/widgets', label: 'Widgets' },
  { to: '/gaming', label: 'Gaming' },
  { to: '/sonidos', label: 'Sonidos' },
  { to: '/beta', label: 'Beta' }
];

// El panel lateral se puede plegar (flecha) para dejar todo el ancho a las
// tablas. Solo aplica en escritorio; en móvil la navegación sigue siendo la
// tira horizontal de siempre. La preferencia se recuerda en localStorage.
const SIDEBAR_KEY = 'backoffice:sidebar-collapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function ChevronIcon({ pointLeft }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform ${pointLeft ? '' : 'rotate-180'}`}
      aria-hidden="true"
    >
      <polyline points="15 6 9 12 15 18" />
    </svg>
  );
}

export default function Layout() {
  const { user, role, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
    } catch (_) {
      // sin almacenamiento: la preferencia dura la sesión
    }
  }, [collapsed]);

  const roleChip = role === 'admin'
    ? <Chip size="sm" color="warning" variant="flat">ADMIN</Chip>
    : role === 'viewer'
      ? <Chip size="sm" color="default" variant="flat">VIEWER</Chip>
      : null;

  return (
    <div className="flex min-h-screen w-screen flex-col overflow-hidden md:h-screen md:flex-row">
      <aside
        className={`relative flex shrink-0 flex-col border-b border-default-200 bg-content1 transition-[width] duration-200 md:border-b-0 md:border-r ${
          collapsed ? 'md:w-12' : 'md:w-56'
        }`}
      >
        {/* Flecha para plegar/desplegar (solo escritorio) */}
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? 'Mostrar panel lateral' : 'Ocultar panel lateral'}
          title={collapsed ? 'Mostrar panel' : 'Ocultar panel'}
          className="absolute -right-3 top-5 z-10 hidden h-6 w-6 items-center justify-center rounded-full border border-default-200 bg-content1 text-default-500 shadow-sm transition hover:border-primary hover:text-primary md:flex"
        >
          <ChevronIcon pointLeft={!collapsed} />
        </button>

        <div className={`flex items-center justify-between gap-3 border-b border-default-200 p-3 md:block md:p-4 ${collapsed ? 'md:hidden' : ''}`}>
          <div>
            <div className="text-xs uppercase tracking-wider text-default-500">
              TikControl
            </div>
            <div className="text-lg font-semibold">Backoffice</div>
          </div>
          <div className="flex items-center gap-2 md:hidden">
            {roleChip}
            <Button size="sm" variant="flat" onPress={logout}>
              Salir
            </Button>
          </div>
        </div>

        {collapsed ? (
          <div className="hidden flex-1 items-start justify-center pt-14 md:flex">
            <span className="select-none text-[10px] font-semibold uppercase tracking-[0.3em] text-default-400 [writing-mode:vertical-rl]">
              Backoffice
            </span>
          </div>
        ) : null}

        <nav className={`flex gap-1 overflow-x-auto p-2 md:flex-1 md:flex-col md:overflow-visible ${collapsed ? 'md:hidden' : ''}`}>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `shrink-0 rounded-md px-3 py-2 text-sm transition ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-default-700 hover:bg-default-100'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={`hidden border-t border-default-200 p-3 md:block ${collapsed ? 'md:hidden' : ''}`}>
          <div className="mb-1 truncate text-xs text-default-500" title={user?.email}>
            {user?.email}
          </div>
          <div className="mb-2">{roleChip}</div>
          <Button size="sm" variant="flat" onPress={logout} fullWidth>
            Cerrar sesión
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}
